import { FC } from 'react';
import { Box, Stack } from '@mui/material';
import Text from '@/components/text';
import Chip from '@/components/chip';
import { startCase } from 'lodash';
import ScrollBox from '@/components/scroll-box';
import { getNiceDate, getNiceNumber, getUserTimezone } from '@/lib/helpers';
import { RequestMetadata } from '@/lib/models/user-event';

interface Props {
  metadata: RequestMetadata;
}

const formatKeyName = (keyName: string): string => {
  switch (keyName) {
    case 'mcpAvailableServers':
      return 'MCP: Available Servers';
    case 'mcpBlockedTools':
      return 'MCP: Blocked Tools';
    default:
      return startCase(keyName);
  }
};

const formatValue = (value: string, type: string) => {
  if (type === 'date') {
    return getNiceDate(value, undefined, getUserTimezone().timezone).formatted;
  }
  if (type === 'number') {
    return getNiceNumber(parseFloat(value));
  }
  if (type === 'boolean') {
    return value === 'true' ? 'Yes' : 'No';
  }
  return value;
};

const MetadataRow: FC<{ keyName: string; value: string | string[]; type: string }> = ({
  keyName,
  value,
  type,
}) => {
  const values = Array.isArray(value) ? value : [value];
  return (
    <Stack direction="column" justifyContent="flex-start" alignItems="flex-start" gap={1} mb={2}>
      <Text>{formatKeyName(keyName)}</Text>
      {values.map((value) => (
        <Chip label={formatValue(value, type)} size="small" key={`${keyName}-${value}`} />
      ))}
    </Stack>
  );
};

const MetadataCard: FC<Props> = ({ metadata }) => {
  const metaDataEntries = Object.entries(metadata);

  return (
    <Box mb={1}>
      <ScrollBox max={6} emptyMessage="There is no metadata available">
        {metaDataEntries.map(([key, { value, type }]) => (
          <MetadataRow key={key} keyName={key} value={value} type={type} />
        ))}
      </ScrollBox>
    </Box>
  );
};

export default MetadataCard;
